import { BaseButton } from '@/components/button/BaseButton'
import { css, Theme } from '@emotion/react'
import { useState } from 'react'
import { useTheme } from '@emotion/react'
import { EllipsisTd } from '../EllipsisTd'

const tableStyles = css`
  border-collapse: collapse;
  white-space: nowrap;
  min-width: 100%;
  th,
  td {
    padding: 8px 16px;
    border: 1px solid #ddd;
  }
  th {
    background-color: #005492;
    color: #ffffff;
    font-family: 'Noto Sans', sans-serif;
    font-weight: 700;
    font-size: 1.2rem;
    text-align: center;
  }
  td {
    white-space: normal;
    word-break: break-word;
    vertical-align: middle;
  }
`
const tdStyle = `
  align-items: center;
  gap: 4px;
  width: 100%;
`
const operationHeaderStyles = css`
  text-align: center !important;
  min-width: 88px;
`
const tableButtonStyles = css`
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 88px;
`
const operationCellStyles = css`
  min-width: 88px;
  white-space: nowrap;
`
const rowStyle = css`
  transition: background-color 0.2s;
`

const isObsoleteStyle = (t: Theme) => css`
  background-color: ${t.c.ec.mediumGray};
`
const iconCellStyle = css`
  display: grid;
  grid-template-columns: 12px 1fr;
`
const iconStyle = css`
  width: 12px;
  height: 12px;
  grid-column: 1;
  margin: auto;
`

const iconDataStyle = css`
  grid-column: 2;
`

const sortableHeaderStyle = css`
  cursor: pointer;
  user-select: none;
`

const headerContentStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`

const sortIconStyle = css`
  width: 10px;
  height: 9px;
  flex-shrink: 0;
  filter: brightness(0) invert(1);
`
export type Column<T> = {
  header: string
  accessor?: (row: T) => React.ReactNode
  text: (row: T) => string
  width?: string
  isDetailLink?: boolean
  sortKey?: string
  isEllipsis?: boolean
  divStyle?: React.CSSProperties
}

export type SortConfig = {
  key: string
  order: 'asc' | 'desc'
}

type Props<T> = {
  data: T[]
  columns: Column<T>[]
  isDelete?: boolean
  onRowDoubleClick?: (row: T) => void
  onDelete?: (row: T) => void
  onSelect?: (row: T) => void
  viewName?: string
  needIcon?: boolean
  sortConfig?: SortConfig
  onSort?: (key: string) => void
  width?: string
}
type TableProps = React.ComponentProps<'table'>
const pendingIcon = '/images/window-restore-icon.svg'
const isObsoletingIcon = '/images/attention-icon.svg'

export const BaseTable = <T extends { id: React.Key }>({
  columns,
  data,
  isDelete = true,
  onRowDoubleClick,
  onDelete,
  onSelect,
  needIcon = false,
  sortConfig,
  onSort,
  width = '100%',
  style,
  ...tableProps
}: Props<T> & TableProps) => {
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null)
  const theme = useTheme()

  return (
    <>
      <table
        css={tableStyles}
        style={{ width: width ?? '100%', ...style }}
        {...tableProps}
      >
        <colgroup>
          {columns.map((col, i) => (
            <col
              key={col.header + i.toString()}
              style={col.width ? { minWidth: col.width } : undefined}
            />
          ))}
        </colgroup>
        <thead>
          <tr>
            {columns.map((col, i) => {
              const isSortable = !!col.sortKey && !!onSort
              const isCurrentSort = sortConfig && sortConfig.key === col.sortKey
              return (
                <th
                  key={col.header + i.toString()}
                  css={isSortable ? sortableHeaderStyle : undefined}
                  onClick={isSortable ? () => onSort(col.sortKey!) : undefined}
                >
                  <div css={isSortable ? headerContentStyle : undefined}>
                    {col.header}
                    {isCurrentSort && (
                      <img
                        src={
                          sortConfig.order === 'desc'
                            ? '/images/sort-down-solid.svg'
                            : '/images/sort-up-solid.svg'
                        }
                        css={sortIconStyle}
                        alt={sortConfig.order === 'desc' ? '降順' : '昇順'}
                      />
                    )}
                  </div>
                </th>
              )
            })}
            {(isDelete || onSelect) && (
              <th css={operationHeaderStyles} key="操作">
                操作
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {data &&
            data.map((row, i) => (
              <tr
                key={row.id + i.toString()}
                css={[
                  rowStyle,
                  'isAlreadyObsolete' in row && row.isAlreadyObsolete === '1'
                    ? isObsoleteStyle(theme)
                    : undefined,
                ]}
              >
                {columns.map((col, j) =>
                  col.isEllipsis ? (
                    <EllipsisTd
                      key={col.header + '_' + j.toString()}
                      text={col.text(row)}
                      divStyle={col.divStyle ?? {}}
                      width={col.width ?? '100%'}
                      onClick={() => setSelectedRowIndex(i)}
                      onDoubleClick={() => onRowDoubleClick?.(row)}
                    />
                  ) : (
                    <td
                      key={col.header + '_' + j.toString()}
                      onClick={() => setSelectedRowIndex(i)}
                      onDoubleClick={() => onRowDoubleClick?.(row)}
                      tabIndex={onRowDoubleClick ? 0 : undefined}
                    >
                      <div
                        css={[needIcon && j === 0 ? iconCellStyle : undefined]}
                        style={{ ...col.divStyle }}
                      >
                        {j === 0 && needIcon ? (
                          <>
                            {'isObsoleting' in row && row.isObsoleting === '1' ? (
                              <img
                                src={isObsoletingIcon}
                                css={iconStyle}
                                alt="廃止予定"
                              />
                            ) : 'hasPendingData' in row &&
                              row.hasPendingData === '1' ? (
                              <img
                                src={pendingIcon}
                                css={iconStyle}
                                alt="適用待ち情報あり"
                              />
                            ) : null}
                            <div style={{ width: '100%' }} css={iconDataStyle}>
                              {col.accessor ? col.accessor(row) : col.text(row)}
                            </div>
                          </>
                        ) : (
                          <div style={{ width: '100%' }}>
                            {col.accessor ? col.accessor(row) : col.text(row)}
                          </div>
                        )}
                      </div>
                    </td>
                  ),
                )}
                {isDelete && (
                  <td css={operationCellStyles}>
                    <div css={tableButtonStyles}>
                      <BaseButton
                        color="danger"
                        size="sm"
                        addStyle={css`
                          white-space: nowrap;
                          min-width: 56px;
                        `}
                        onClick={() => {
                          onDelete?.(row)
                        }}
                      >
                        削除
                      </BaseButton>
                    </div>
                  </td>
                )}
                {!isDelete && onSelect && (
                  <td css={operationCellStyles}>
                    <div css={tableButtonStyles}>
                      <BaseButton
                        color="primary"
                        size="sm"
                        addStyle={css`
                          white-space: nowrap;
                          min-width: 56px;
                        `}
                        onClick={() => {
                          onSelect?.(row)
                        }}
                      >
                        選択
                      </BaseButton>
                    </div>
                  </td>
                )}
              </tr>
            ))}
        </tbody>
      </table>
    </>
  )
}
